import React from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { FaFileInvoice, FaListUl, FaChartPie, FaWarehouse, FaMapMarkedAlt } from "react-icons/fa";

function BillingReports() {
  const navigate = useNavigate();

  const reports = [
    {
      title: "Order Intelligence Summary",
      desc: "High-level audit of all order headers with fiscal totals and logistics status.",
      path: "/reports/order-head",
      icon: <FaFileInvoice size={24} />,
      color: "#6366f1"
    },
    {
      title: "Sales Line Manifest",
      desc: "Granular breakdown of every SKU movement across all approved transactions.",
      path: "/reports/order-lines",
      icon: <FaListUl size={24} />,
      color: "#8b5cf6"
    },
    {
      title: "DS Deployment Logs",
      desc: "Real-time records of salesman field activity, outlet orders, and productivity.",
      path: "/ds-order-report",
      icon: <FaMapMarkedAlt size={24} />,
      color: "#ec4899"
    },
    {
        title: "Topology Audit (Travel)",
        desc: "Sequence-based travel logs with GPS verification and visit efficiency analysis.",
        path: "/ds-travel-report",
        icon: <FaRoute size={24} />,
        color: "#f59e0b"
    },
    {
        title: "Inventory Stock Ledger",
        desc: "Master stock movements including opening, inward infusions, and field sales.",
        path: "/distributor-inventory",
        icon: <FaWarehouse size={24} />,
        color: "#10b981"
    }
  ];

  return (
    <Layout>
      <div className="layout-content">
        <div style={{ marginBottom: 40 }}>
          <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>Intelligence Command</h1>
          <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Select an analytical module to audit your distribution network and financial performance.</p>
        </div>

        <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", 
            gap: 24 
        }}>
          {reports.map((report, idx) => (
            <div
              key={idx}
              onClick={() => navigate(report.path)}
              className="premium-card"
              style={{
                cursor: "pointer",
                padding: 32,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                display: "flex",
                flexDirection: "column",
                gap: 16,
                border: "1px solid var(--border)",
                position: "relative",
                overflow: "hidden"
              }}
              onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px)";
                  e.currentTarget.style.borderColor = report.color;
                  e.currentTarget.style.boxShadow = `0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px ${report.color}15`;
              }}
              onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.borderColor = "var(--border)";
                  e.currentTarget.style.boxShadow = "var(--shadow-sm)";
              }}
            >
              <div style={{ 
                  width: 56, 
                  height: 56, 
                  borderRadius: 14, 
                  background: `${report.color}10`, 
                  color: report.color, 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center",
                  marginBottom: 8
              }}>
                {report.icon}
              </div>
              <div>
                <h3 style={{ margin: "0 0 8px 0", fontSize: 18, fontWeight: 800, color: "var(--text-main)" }}>{report.title}</h3>
                <p style={{ margin: 0, fontSize: 14, color: "var(--text-muted)", lineHeight: 1.6 }}>{report.desc}</p>
              </div>
              <div style={{ 
                  marginTop: "auto", 
                  paddingTop: 16, 
                  display: "flex", 
                  alignItems: "center", 
                  gap: 8, 
                  fontSize: 12, 
                  fontWeight: 800, 
                  color: report.color,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em"
              }}>
                Launch Protocol <span>→</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}

// Helper icons for Missing Routes (since I used FaRoute)
const FaRoute = (props) => (
    <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height={props.size} width={props.size} {...props}>
        <path d="M432 288c-44.18 0-80 35.82-80 80 0 18.25 6.13 35.06 16.44 48.47L293.7 483.7c-6.25 6.25-16.38 6.25-22.62 0l-31.1-31.1C238.2 443.7 205.8 400 160 400c-44.18 0-80 35.82-80 80s35.82 80 80 80c44.18 0 80-35.82 80-80 0-18.25-6.13-35.06-16.44-48.47l74.73-67.24c6.25-6.25 16.38-6.25 22.62 0l31.1 31.1c1.78 1.78 34.2 45.62 80 45.62 44.18 0 80-35.82 80-80s-35.82-80-80-80zm0 128c-26.51 0-48-21.49-48-48s21.49-48 48-48 48 21.49 48 48-21.49 48-48 48zM160 528c-26.51 0-48-21.49-48-48s21.49-48 48-48 48 21.49 48 48-21.49 48-48 48zM448 0H64C28.65 0 0 28.65 0 64v288c0 35.35 28.65 64 64 64h42.6c24.3-51.4 75.8-88 136.1-88 33.7 0 64.9 11.2 90 30l45.4-45.4C320.7 217.4 288 190.1 288 160c0-44.2 35.8-80 80-80s80 35.8 80 80c0 30.1-32.7 57.4-54.1 76.6l43 43C481.5 259.9 512 213.6 512 160V64c0-35.35-28.65-64-64-64zm-80 208c-26.5 0-48-21.5-48-48s21.5-48 48-48 48 21.5 48 48-21.5 48-48 48z"></path>
    </svg>
);

export default BillingReports;
